import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";
import { useSelector } from "react-redux";
import { selectCurrentUser } from "../../redux/authentication/authSlice";
import { UserGroup } from "../../common/User";
import { NavLink } from "react-router-dom";
import { JobRatingLabel } from "../../common/JobOrder";
import { formatDateString } from "../../common/FormatDateString";
import { ComplianceRosterResponse, ComplianceRosterRow } from "../../common/ComplianceTraining";
import "./ComplianceTrainingRosterForm.scss";

interface ComplianceTrainingRosterFormProps {
  rosterResponse: ComplianceRosterResponse | undefined;
}

const ComplianceTrainingRosterForm = ({ rosterResponse }: ComplianceTrainingRosterFormProps) => {
  const user = useSelector(selectCurrentUser);
  const isViewOnly =
    (user?.userGroup as UserGroup) === UserGroup.VESSEL_EXECS ||
    (user?.userGroup as UserGroup) === UserGroup.FLEET_EXECS;

  const trainings = rosterResponse?.trainings ?? [];

  const rows: ComplianceRosterRow[] = rosterResponse?.rows ?? [];

  const formatValidity = (months: number | null): string => {
    if (months === null) return "Every Sign-on";
    if (months < 12) return `${months} Month${months === 1 ? "" : "s"}`;
    const years = months / 12;
    const display = Number.isInteger(years) ? years : parseFloat(years.toFixed(1));
    return `${display} Year${display === 1 ? "" : "s"}`;
  };

  const getExpirationClass = (dateStr: string | null | undefined): string => {
    if (!dateStr) return "compliance-expired-flag";
    const expiration = new Date(dateStr);
    const now = new Date();
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(now.getDate() + 30);
    if (expiration < now) return "compliance-expired-flag";
    if (expiration <= thirtyDaysFromNow) return "expiring-soon-flag";
    return "";
  };

  return (
    <div>
      {rosterResponse && (
        <TableContainer className="roster-table compliance-training-roster-component">
          <Table className="table">
            <TableHead>
              <TableRow>
                <TableCell style={{ width: "75px" }}>Rating</TableCell>
                <TableCell>Mariner's Name</TableCell>
                <TableCell>Date Joined</TableCell>
                {trainings.map((ct) => (
                  <TableCell
                    key={ct.id}
                    style={{ minWidth: "50px" }}
                  >
                    {ct.name}
                  </TableCell>
                ))}
              </TableRow>
              <TableRow>
                <TableCell />
                <TableCell />
                <TableCell />
                {trainings.map((ct) => (
                  <TableCell key={ct.id} align="center" className="validity-period-cell">
                    {formatValidity(ct.validityInMonths)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => (
                <TableRow key={"complianceRoster" + index}>
                  <TableCell className="wb-wrap">
                    <Tooltip
                      title={row.rating ? (JobRatingLabel as Record<string, string>)[row.rating] ?? row.rating : ""}
                      placement="right"
                    >
                      <span>{row.rating ? row.rating.substring(1) : ""}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    {isViewOnly ? (
                      <p>
                        {[row.firstName, row.middleName, row.lastName]
                          .filter(Boolean)
                          .join(" ")}
                      </p>
                    ) : (
                      <NavLink
                        className="blue-link table-link"
                        style={{ textAlign: "left" }}
                        to={`/profiles/${row.profileId}`}
                      >
                        {[row.firstName, row.middleName, row.lastName]
                          .filter(Boolean)
                          .join(" ")}
                      </NavLink>
                    )}
                  </TableCell>
                  <TableCell>
                    <TextField 
                      type='date'
                      disabled
                      value={!!row.startDate ? row.startDate : ""}
                    />
                  </TableCell>
                  {trainings.map((ct) => {
                    const notApplicable = row.notApplicableTrainingIds?.includes(ct.id);
                    const dateStr = row.expirationDates[ct.id] ?? null;
                    const hasRecord = ct.id in row.expirationDates;

                    if (notApplicable && !hasRecord) {
                      return <TableCell key={ct.id} align="center">N/A</TableCell>;
                    }
                    return (
                      <TableCell
                        key={ct.id}
                        align="center"
                        className={notApplicable ? "" : (dateStr ? getExpirationClass(dateStr) : "compliance-expired-flag")}
                      >
                        {dateStr ? formatDateString(dateStr) : ""}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {rosterResponse && (
        <div className="compliance-roster-legend">
          <span className="legend-item compliance-expired-flag">Expired/Missing</span>
          <span className="legend-item expiring-soon-flag">Expiring within 30 days</span>
        </div>
      )}
    </div>
  );
};

export default ComplianceTrainingRosterForm;
